import React from 'react';
import { TableRow, TableCell, IconButton, Tooltip, Typography } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import type { LegacyAuditTableRowProps } from './types';
import { formatCurrency, formatDateMMDDYYYY } from '../../utils/formatUtils';
import {
  prepareLottoSalesDetails,
  preparePayoutDetails,
  prepareScratcherCountsDetails,
  prepareScratcherSalesDetails,
} from './utils/legacyAuditDiscrepancyDetails';
import { isLottoSummaryAllZero, isPosSummaryAllZero } from './utils/legacyAuditZeroChecks';
import {
  getLegacyAuditTableColumnCount,
  getStoreConfigurationFlags,
} from './utils/legacyAuditStoreConfiguration';
import LegacyAuditConditionalColumns from './components/LegacyAuditConditionalColumns';
import LegacyAuditReconciliationDisplay from './components/LegacyAuditReconciliationDisplay';
import LegacyChildRows from './LegacyChildRows';
import { Icon } from '@iconify/react';

const LegacyAuditTableRow: React.FC<LegacyAuditTableRowProps> = ({
  summary,
  rowIdx,
  isExpanded,
  onExpandClick,
  selectedStore,
  handleOpenReconcile
}) => {
  // Extract conditional logic to top level for better readability
  const {
    isLottoAuthorized,
    hasPosType,
    isBothConnected,
    integrationCase
  } = getStoreConfigurationFlags(selectedStore);

  // Transform daily summary to match reference format
  const row = {
    report_date: summary.report_date,
    pos_summary: summary.daily_totals?.pos ? {
      scratcher_quantity_sold: summary.daily_totals.pos.scratcher_count ?? 0,
      scratcher_sales: summary.daily_totals.pos.scratcher_sales ?? 0,
      lotto_sales: summary.daily_totals.pos.lotto_sales ?? 0,
      payout: summary.daily_totals.pos.payout ?? 0,
    } : undefined,
    audit_summary: summary.daily_totals?.audit ? {
      total_sold: summary.daily_totals.audit.total_sold ?? 0,
      revenue: summary.daily_totals.audit.total_revenue ?? 0,
      opening_count: summary.daily_totals.audit.opening_count ?? 0,
      newly_activated_count: summary.daily_totals.audit.newly_activated_count ?? 0,
      moved_to_backstock_count: summary.daily_totals.audit.moved_to_backstock_count ?? 0,
      closing_count: summary.daily_totals.audit.closing_count ?? 0,
      audit_count: summary.audits.length,
    } : undefined,
    lotto_summary: summary.daily_totals?.lotto,
    reconciliation_variance: summary.daily_totals?.reconciliation_variance,
    audits: summary.audits,
  };

  // Get component values directly from response data
  const posScratcherQuantity = Number(row?.pos_summary?.scratcher_quantity_sold ?? 0);
  const totalSold = Number(row?.audit_summary?.total_sold ?? 0);
  const posScratcherSales = Number(row?.pos_summary?.scratcher_sales ?? 0);
  const revenue = Number(row?.audit_summary?.revenue ?? 0);
  const posLottoSales = Number(row?.pos_summary?.lotto_sales ?? 0);
  const lottoSales = Number(row?.lotto_summary?.sales ?? 0);
  const lottoPayoutsTotal = Number(row?.lotto_summary?.payouts_total ?? 0);
  const freeTicketsAmount = Number(row?.lotto_summary?.scratcher_free_tickets_paid_amount ?? 0);
  const posPayout = Number(row?.pos_summary?.payout ?? 0);

  // Get variance values directly from backend response and convert to numbers
  const scratcherQuantityVariance = row?.reconciliation_variance?.scratcher?.quantity_variance != null
    ? Number(row.reconciliation_variance.scratcher.quantity_variance)
    : undefined;
  const scratcherRevenueVariance = row?.reconciliation_variance?.scratcher?.revenue_variance;
  const lottoSalesVariance = row?.reconciliation_variance?.lotto_sales_variance;
  const payoutVariance = row?.reconciliation_variance?.payout_variance;
  const totalVariance = row?.reconciliation_variance?.total_variance;

  // Check zero states using component values directly
  const isLottoZero = isLottoSummaryAllZero(row?.lotto_summary);
  const isPosZero = isPosSummaryAllZero(row?.pos_summary);

  // Prepare discrepancy details using backend data directly
  const scratcherCountsDetails = prepareScratcherCountsDetails(
    posScratcherQuantity,
    totalSold,
    scratcherQuantityVariance,
    isPosZero,
    isLottoZero,
    hasPosType,
    isBothConnected
  );

  const scratcherSalesDetails = prepareScratcherSalesDetails(
    posScratcherSales,
    revenue,
    scratcherRevenueVariance,
    isPosZero,
    isLottoZero,
    hasPosType,
    isBothConnected
  );

  const lottoSalesDetails = prepareLottoSalesDetails(
    posLottoSales,
    lottoSales,
    lottoSalesVariance,
    isPosZero,
    isLottoZero,
    hasPosType,
    isBothConnected
  );

  const payoutDetails = preparePayoutDetails(
    lottoPayoutsTotal,
    freeTicketsAmount,
    posPayout,
    payoutVariance,
    isPosZero,
    isLottoZero,
    hasPosType,
    isBothConnected
  );

  // Use total_variance directly from backend response
  const totalDiscrepancy = totalVariance !== undefined && totalVariance !== null
    ? totalVariance
    : 0;

  // Simple reconciliation check (can be enhanced later)
  const reconciliation = null;

  const handleDateClick = () => {
    if (handleOpenReconcile) {
      handleOpenReconcile(summary);
    }
  };

  const handleReconcile = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (handleOpenReconcile) {
      handleOpenReconcile(summary);
    }
  };

  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    onExpandClick(rowIdx);
  };

  const tableColumnCount = getLegacyAuditTableColumnCount(selectedStore);
  const groupBorderStyle = { borderRight: '1px solid #A3AED0CC' };
  const innerBorderStyle = { borderRight: '1px solid #A3AED03D' };
  const rowBackgroundColor = rowIdx % 2 === 0 ? '#FAF8FFCC' : '#FFFFFF';

  return (
    <React.Fragment>
      <TableRow
        className={rowIdx % 2 === 0 ? "audit-table__row--even cursor-pointer" : "audit-table__row--odd cursor-pointer"}
        onClick={handleDateClick}
        sx={{ bgcolor: rowBackgroundColor }}
      >
        <TableCell
          sx={{
            width: 280,
            minWidth: 280,
            position: 'sticky',
            left: 0,
            zIndex: 1,
            bgcolor: rowBackgroundColor,
            textAlign: 'left',
            ...groupBorderStyle,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', height: '54px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                size="small"
                onClick={handleExpand}
                sx={{ marginRight: '2px !important', padding: '4px' }}
              >
                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
              <Typography
                variant="body2"
                style={{}}
                onClick={handleReconcile}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  lineHeight: '20px',
                  letterSpacing: 0,
                  verticalAlign: 'middle',
                  color: '#2C6CA0',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  margin: 0
                }}
              >
                {formatDateMMDDYYYY(row?.report_date)}
              </Typography>
            </div>

            {/* Conditionally render status section based on store configuration */}
            {(isLottoAuthorized || hasPosType) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* Show Lotto status only if lotto is authorized */}
                {isLottoAuthorized && isLottoZero && (
                  <Tooltip title="Lotto data pending">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon icon="twemoji:hourglass-done" width="16" height="16" />
                      <span style={{ fontSize: '0.75rem' }}>Lotto</span>
                    </div>
                  </Tooltip>
                )}

                {/* Show divider only if both Lotto and POS are present */}
                {isLottoAuthorized && hasPosType && isLottoZero && isPosZero && (
                  <div style={{ width: '1px', height: '16px', backgroundColor: '#ddd' }} />
                )}

                {/* Show POS status only if POS type is valid */}
                {hasPosType && isPosZero && (
                  <Tooltip title="POS data pending">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon icon="twemoji:hourglass-done" width="16" height="16" />
                      <span style={{ fontSize: '0.75rem' }}>POS</span>
                    </div>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        </TableCell>

        <TableCell sx={{ textAlign: 'right', ...groupBorderStyle }}>
          {row?.audit_summary?.audit_count ?? 0}
        </TableCell>

        <TableCell sx={{ textAlign: 'right', ...innerBorderStyle }}>
          {Number(row?.audit_summary?.opening_count ?? 0)}
        </TableCell>

        <TableCell sx={{ textAlign: 'right', ...innerBorderStyle }}>
          {Number(row?.audit_summary?.newly_activated_count ?? 0)}
        </TableCell>

        <TableCell sx={{ textAlign: 'right', ...innerBorderStyle }}>
          {totalSold}
        </TableCell>

        <TableCell sx={{ textAlign: 'right', ...innerBorderStyle }}>
          {Number(row?.audit_summary?.moved_to_backstock_count ?? 0)}
        </TableCell>

        <TableCell sx={{ textAlign: 'right', ...innerBorderStyle }}>
          {Number(row?.audit_summary?.closing_count ?? 0)}
        </TableCell>

        <TableCell sx={{ textAlign: 'right', ...groupBorderStyle }}>
          {formatCurrency(revenue)}
        </TableCell>

        <LegacyAuditConditionalColumns
          integrationCase={integrationCase}
          scratcherCountsDetails={scratcherCountsDetails}
          lottoSalesDetails={lottoSalesDetails}
          scratcherSalesDetails={scratcherSalesDetails}
          payoutDetails={payoutDetails}
          totalDiscrepancy={totalDiscrepancy}
          groupBorderColor="#A3AED0CC"
          innerBorderColor="#A3AED03D"
        />

        <TableCell sx={{ textAlign: 'center', width: 140 }}>
          <LegacyAuditReconciliationDisplay
            totalDiscrepancy={totalDiscrepancy}
            reconciliation={reconciliation}
            onReconcile={handleReconcile}
            showDiscrepancy={isBothConnected}
          />
        </TableCell>
      </TableRow>

      {isExpanded && (
        <LegacyChildRows
          audits={row.audits}
          selectedStore={selectedStore}
          tableColumnCount={tableColumnCount}
          hasPosType={hasPosType}
          parentRowBackgroundColor={rowBackgroundColor}
        />
      )}
    </React.Fragment>
  );
};

export default LegacyAuditTableRow;
